import LogoPrimary from '@tenant/assets/logo-primary.svg';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { redirectToDocs } from 'utils/redirectToDocs';

import { SmsValidate } from '@pages/SmsValidate';
import { TOTPValidate } from '@pages/TOTPValidate';

import { useMfaSettings } from 'hooks/useMfaSettings';
import { useRedirectTo } from 'hooks/useRedirectTo';

import { Spinner } from '@atoms/Spinner/Spinner';

import { AuthenticatorOption, SMSOption } from './MfaOptions';

type MfaMethod = 'authenticator' | 'sms';

export const MfaVerify = () => {
  const { totpEnabled, loading, hasPhone } = useMfaSettings();

  const { navigateWithRedirectTo } = useRedirectTo();

  const [searchParams, setSearchParams] = useSearchParams();

  const fromDocsFlag = searchParams.get('fromDocs') ?? null;

  const [selectedOption, setSelectedOption] = useState<MfaMethod | undefined>(
    () => {
      const method = searchParams.get('method') as MfaMethod;
      return ['authenticator', 'sms'].includes(method) ? method : undefined;
    }
  );

  useEffect(() => {
    if (!totpEnabled && !hasPhone) {
      navigateWithRedirectTo('/mfa-setup');
    }
  }, [hasPhone, navigateWithRedirectTo, totpEnabled]);

  useEffect(() => {
    const method = searchParams.get('method') as MfaMethod;
    setSelectedOption(
      ['authenticator', 'sms'].includes(method) ? method : undefined
    );
  }, [searchParams]);

  const handleOptionSelect = useCallback(
    (option: 'authenticator' | 'sms') => {
      setSelectedOption(option);
      setSearchParams({
        method: option,
        ...(fromDocsFlag && { fromDocs: 'true' })
      });
    },
    [fromDocsFlag, setSearchParams]
  );

  const onSuccess = useCallback(() => {
    if (fromDocsFlag) {
      redirectToDocs();
      return;
    }

    navigateWithRedirectTo('/login_transition', { state: { loggingIn: true } });
  }, [fromDocsFlag, navigateWithRedirectTo]);

  useEffect(() => {
    if (loading) {
      return;
    }

    // if only TOTP is enabled
    if (totpEnabled && !hasPhone) {
      handleOptionSelect('authenticator');
    }

    // if user only has SMS option
    if (hasPhone && !totpEnabled) {
      handleOptionSelect('sms');
    }
  }, [handleOptionSelect, hasPhone, loading, totpEnabled]);

  if (loading) {
    return <Spinner />;
  }

  return (
    <main className="h-screen overflow-y-auto">
      <div className="flex w-full justify-center p-4 md:p-10">
        <LogoPrimary className="hidden h-auto w-24 md:block" />
      </div>
      <div className="flex items-center justify-center">
        <div className="size-full bg-white p-8 md:size-fit md:rounded-2xl md:px-40">
          {!selectedOption ? (
            <div className="grid grid-cols-2 gap-4">
              {totpEnabled && (
                <AuthenticatorOption
                  onClick={() => handleOptionSelect('authenticator')}
                />
              )}
              {hasPhone && (
                <SMSOption onClick={() => handleOptionSelect('sms')} />
              )}
            </div>
          ) : (
            <>
              {selectedOption === 'authenticator' && (
                <TOTPValidate onSuccess={onSuccess} />
              )}

              {selectedOption === 'sms' && hasPhone && (
                <SmsValidate onSuccess={onSuccess} />
              )}
            </>
          )}
        </div>
      </div>
    </main>
  );
};
